import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import { categoryMap } from '@/mocks/categories';
import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { siteInfo } from '@/mocks/siteInfo';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100">
              <i className="ri-file-unknow-line text-3xl text-secondary-500"></i>
            </div>
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-2">文章未找到</h2>
            <p className="text-foreground-600 mb-6">该文章可能已被移除或链接无效</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-primary-500 text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-home-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const categoryName = categoryMap[article.category] || '全部作品';
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    datePublished: article.publishedAt,
    author: {
      '@type': 'Organization',
      name: siteInfo.name,
    },
    publisher: {
      '@type': 'Organization',
      name: siteInfo.name,
      url: siteInfo.url,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteInfo.url}/article/${article.id}`,
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `大桥未久${article.title}的发布时间是什么？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `本文发布于 ${article.publishedAt}，属于大桥未久${categoryName}分类。`,
        },
      },
      {
        '@type': 'Question',
        name: '如何查看大桥未久的更多作品内容？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: `您可以通过大桥未久官方专区的${categoryName}分类浏览更多相关内容，或访问首页查看精选推荐。`,
        },
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SEO
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`${siteInfo.url}/article/${article.id}`}
        ogType="article"
        ogImage={article.coverImage}
        schemaData={articleSchema}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="bg-background-100 border-b border-background-200/70">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryName, href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>

          <article className="py-8 md:py-12 px-4 md:px-6 max-w-4xl mx-auto">
            <header className="mb-8">
              <div className="flex items-center gap-3 text-xs text-foreground-500 mb-4 flex-wrap">
                <Link
                  to={`/category/${article.category}`}
                  className="bg-accent-100 text-accent-700 px-3 py-1 rounded-full font-medium hover:bg-accent-200 transition-colors whitespace-nowrap"
                >
                  {categoryName}
                </Link>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i> {article.publishedAt}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i> {article.views.toLocaleString()} 次阅读
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i> 阅读约 {article.readTime} 分钟
                </span>
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>

              <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
                {article.excerpt}
              </p>

              <div className="flex items-center gap-2 mt-4 flex-wrap">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs bg-secondary-100 text-secondary-700 px-2 py-0.5 rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </header>

            <div className="mb-10 rounded-lg overflow-hidden">
              <img
                src={article.coverImage}
                alt={article.title}
                title={`${article.title} - 大桥未久官方专区`}
                className="w-full h-auto object-cover rounded-lg"
              />
            </div>

            <div
              className="prose prose-lg max-w-none prose-headings:font-heading prose-headings:text-foreground-900 prose-p:text-foreground-700 prose-p:leading-relaxed prose-h4:text-xl prose-h4:font-bold prose-h4:mt-8 prose-h4:mb-4 prose-p:mb-4"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-12 pt-8 border-t border-background-200/70">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <p className="text-sm text-foreground-500">觉得这篇内容有帮助？</p>
                  <p className="text-sm text-foreground-600">分享给更多大桥未久的粉丝吧</p>
                </div>
                <div className="flex items-center gap-2">
                  <button className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-foreground-600 hover:bg-primary-500 hover:text-white transition-colors cursor-pointer whitespace-nowrap" aria-label="分享到微博">
                    <i className="ri-weibo-line text-lg"></i>
                  </button>
                  <button className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-foreground-600 hover:bg-primary-500 hover:text-white transition-colors cursor-pointer whitespace-nowrap" aria-label="分享到微信">
                    <i className="ri-wechat-line text-lg"></i>
                  </button>
                  <button className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-foreground-600 hover:bg-primary-500 hover:text-white transition-colors cursor-pointer whitespace-nowrap" aria-label="复制链接">
                    <i className="ri-link text-lg"></i>
                  </button>
                </div>
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
              <div className="max-w-7xl mx-auto">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8">
                  相关推荐
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {relatedArticles.map((related) => (
                    <Link
                      key={related.id}
                      to={`/article/${related.id}`}
                      className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all"
                    >
                      <div className="relative h-44 overflow-hidden">
                        <img
                          src={related.coverImage}
                          alt={related.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <div className="text-xs text-foreground-500 mb-1">{related.publishedAt}</div>
                        <h3 className="text-sm font-heading font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2">
                          {related.title}
                        </h3>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}

          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
          />
        </main>
        <Footer />
      </div>
    </>
  );
}